import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Button,
  Chip,
  Divider,
  Link,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PoliceReportLookup } from '../../services/types/police-report-types';
import {
  StyledBodyCell as BaseStyledBodyCell,
  StyledBodyRow,
  StyledHeaderCell,
  StyledHeaderRow,
  StyledTable,
  StyledTableContainer,
} from '../matters/MattersTable.styles';
import { usePoliceReportsColumns } from './PoliceReportsTable.config';

// Override the last column styles for police reports to match the opacity of other columns
const PoliceReportsStyledHeaderCell = styled(StyledHeaderCell)(({ theme }) => ({
  '&:last-of-type': {
    opacity: 0.5,
  },
}));

const StyledBodyCell = styled(BaseStyledBodyCell)(({ theme }) => ({
  '&:last-of-type': {
    opacity: 0.5,
  },
}));

interface PoliceReportsTableProps {
  data: PoliceReportLookup[];
}

const FilterMenu = ({ table }: { table: Table<PoliceReportLookup> }) => {
  const filterableColumns = table
    .getAllColumns()
    .filter((column) => column.getCanFilter());

  const uniqueValues: Record<string, string[]> = useMemo(() => {
    return Object.fromEntries(
      filterableColumns.map((column) => [
        column.id,
        Array.from(column.getFacetedUniqueValues().keys()).sort(),
      ]),
    );
  }, [filterableColumns]);

  const handleClearAll = () => {
    table.setColumnFilters([]);
  };

  return (
    <Stack spacing={2} px={2} py={1} minWidth={300}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h2">Filters</Typography>
        <Button onClick={handleClearAll} variant="text" size="small">
          Clear All
        </Button>
      </Stack>
      <Divider />
      {filterableColumns.map((column) => {
        const currentValues = (column.getFilterValue() as string[]) || [];
        return (
          <Stack key={column.id} spacing={3}>
            <Typography variant="h3" sx={{ opacity: 0.5 }}>
              {column.columnDef.header?.toString()}
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={uniqueValues[column.id]}
              value={currentValues}
              popupIcon={null}
              disableCloseOnSelect
              onChange={(_, newValue) => {
                table.setColumnFilters((old) => [
                  ...old.filter((f) => f.id !== column.id),
                  ...(newValue.length > 0
                    ? [{ id: column.id, value: newValue }]
                    : []),
                ]);
              }}
              renderInput={(params) => <TextField {...params} />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                  />
                ))
              }
            />
          </Stack>
        );
      })}
    </Stack>
  );
};

export const PoliceReportsTable: React.FC<PoliceReportsTableProps> = ({
  data,
}) => {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<any[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const columns = usePoliceReportsColumns();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
      columnFilters,
      sorting,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    enableGlobalFilter: true,
  });

  const handleRowClick = (matterId: string | undefined, taskId: string) => {
    if (matterId && taskId) {
      navigate({
        pathname: `/matters/${matterId}/`,
        search: `?task_id=${taskId}`,
      });
    }
  };

  const handleFilterButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);
    setFilterMenuOpen(true);
  };

  const handleFilterMenuClose = () => {
    setAnchorEl(null);
    setFilterMenuOpen(false);
  };

  return (
    <Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        px={6}
        pt={4}
        bgcolor={(theme) => theme.palette.backgroundGray?.light || '#f7f7f7'}
      >
        <Typography variant="h1" flexGrow={1}>
          Police Report Lookups
        </Typography>
        <Stack
          flexGrow={2}
          direction="row"
          alignItems="center"
          spacing={4}
          justifyContent="flex-end"
        >
          <TextField
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon /> }}
            placeholder="Search police reports"
            sx={{ minWidth: 300 }}
          />
        </Stack>
      </Stack>
      <StyledTableContainer>
        <StyledTable>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <StyledHeaderRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <PoliceReportsStyledHeaderCell
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler?.()}
                    style={{
                      cursor: header.column.getCanSort()
                        ? 'pointer'
                        : 'default',
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getIsSorted() === 'asc'
                      ? ' ↑'
                      : header.column.getIsSorted() === 'desc'
                        ? ' ↓'
                        : ''}
                  </PoliceReportsStyledHeaderCell>
                ))}
              </StyledHeaderRow>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <StyledBodyRow
                key={row.id}
                hover
                style={{
                  cursor:
                    row.original.matter_id && row.original.task_id
                      ? 'pointer'
                      : 'default',
                }}
                onClick={() =>
                  handleRowClick(row.original.matter_id, row.original.task_id)
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <StyledBodyCell key={cell.id}>
                    {cell.column.id === 'matter_name' &&
                    row.original.matter_id &&
                    row.original.matter_name ? (
                      <Link
                        href={`/matters/${row.original.matter_id}`}
                        onClick={(e) => e.stopPropagation()}
                        underline="hover"
                      >
                        {row.original.matter_name}
                      </Link>
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </StyledBodyCell>
                ))}
              </StyledBodyRow>
            ))}
          </tbody>
        </StyledTable>
      </StyledTableContainer>
    </Stack>
  );
};
